import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { AppLayout, PageHeader } from "./_shared/AppLayout";
import { brl } from "./_shared/utils";
import { useAppData } from "./_shared/store";
import { AcaoModal, CryptoModal, CdiModal } from "./_shared/InvestimentoModals";
import {
  Plus, Pencil, Trash2, TrendingUp, TrendingDown,
  RefreshCw, ChevronLeft, BarChart3, Building2, Bitcoin, Landmark,
} from "lucide-react";
import { toast } from "sonner";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Hooks de cotações ─────────────────────────────────────────
function isMercadoAberto() {
  const brt = new Date(new Date().toLocaleString("en-US", { timeZone: "America/Sao_Paulo" }));
  const day = brt.getDay();
  if (day === 0 || day === 6) return false;
  const mins = brt.getHours() * 60 + brt.getMinutes();
  return mins >= 600 && mins < 1050; // 10:00–17:30
}

function useBrapiQuotes(tickers) {
  const [quotes, setQuotes]     = useState({});
  const [loading, setLoading]   = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [marketOpen, setMarketOpen] = useState(isMercadoAberto());
  const [error, setError]       = useState(false);
  const key = [...tickers].sort().join(",");

  const doFetch = useCallback(async () => {
    if (!tickers.length) return;
    setMarketOpen(isMercadoAberto());
    setLoading(true);
    try {
      const res = await axios.get(
        `${API_BASE}/quotes/stocks?tickers=${tickers.join(",")}`,
        { timeout: 10000 }
      );
      setQuotes(res.data || {});
      setLastUpdate(new Date());
      setError(false);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [key]); // eslint-disable-line

  useEffect(() => {
    doFetch();
    const interval = setInterval(doFetch, 60000);
    return () => clearInterval(interval);
  }, [key]); // eslint-disable-line

  return { quotes, loading, lastUpdate, marketOpen, error, refetch: doFetch };
}

function useBrapiDividends(items) {
  // items: [{ ticker, dataCompra, qtd }]
  const [autoDiv, setAutoDiv] = useState({});
  const key = items.map((i) => `${i.ticker}|${i.dataCompra || ""}|${i.qtd}`).sort().join(",");

  const doFetch = useCallback(async () => {
    const tickers = [...new Set(items.map((i) => i.ticker))];
    if (!tickers.length) return;
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/dividends?tickers=${tickers.join(",")}`,
        { timeout: 15000 }
      );
      const divMap = res.data || {};
      const map = {};
      items.forEach(({ ticker, dataCompra, qtd }) => {
        const events = divMap[ticker] || [];
        const cutoff = dataCompra ? new Date(dataCompra + "T00:00:00") : null;
        map[ticker] = events
          .filter((d) => !cutoff || new Date(d.paymentDate) >= cutoff)
          .reduce((s, d) => s + Number(d.rate || 0) * qtd, 0);
      });
      setAutoDiv(map);
    } catch {
      // keep cached
    }
  }, [key]); // eslint-disable-line

  useEffect(() => {
    doFetch();
    const t = setInterval(doFetch, 4 * 60 * 60 * 1000); // every 4h
    return () => clearInterval(t);
  }, [key]); // eslint-disable-line

  return autoDiv;
}

// ─── Mapeamento symbol → CoinGecko ID ────────────────────────
const SYMBOL_TO_GECKO = {
  BTC: "bitcoin", ETH: "ethereum", BNB: "binancecoin",
  SOL: "solana", ADA: "cardano", DOT: "polkadot",
  USDT: "tether", USDC: "usd-coin", XRP: "ripple",
  MATIC: "matic-network", AVAX: "avalanche-2", LINK: "chainlink",
  DOGE: "dogecoin", SHIB: "shiba-inu", UNI: "uniswap",
  ATOM: "cosmos", LTC: "litecoin", TON: "the-open-network",
  NEAR: "near", ARB: "arbitrum",
};

async function fetchCryptoPricesFromCoinGecko(geckoIdToSymbol) {
  const ids = Object.keys(geckoIdToSymbol).join(",");
  if (!ids) return {};
  const res = await axios.get(
    `${API_BASE}/quotes/crypto?ids=${ids}`,
    { timeout: 15000 }
  );
  const map = {};
  for (const [geckoId, data] of Object.entries(res.data || {})) {
    const sym = geckoIdToSymbol[geckoId];
    if (sym && data.brl > 0) {
      map[sym] = { price: data.brl, change24h: data.brl_24h_change ?? null };
    }
  }
  return map;
}

// items: [{ geckoId, symbol }]  — quotes retornado é keyed by symbol
function useCryptoQuotes(items) {
  const [quotes, setQuotes]     = useState({});
  const [loading, setLoading]   = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [error, setError]       = useState(false);
  const key = items.map((i) => `${i.geckoId}|${i.symbol}`).sort().join(",");

  const geckoIdToSymbol = {};
  items.forEach(({ geckoId, symbol }) => { if (geckoId && symbol) geckoIdToSymbol[geckoId] = symbol; });

  const doFetch = useCallback(async () => {
    if (!Object.keys(geckoIdToSymbol).length) return;
    setLoading(true);
    try {
      const map = await fetchCryptoPricesFromCoinGecko(geckoIdToSymbol);
      if (map && Object.keys(map).length) {
        setQuotes(map);
        setLastUpdate(new Date());
        setError(false);
      } else {
        setError(true);
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [key]); // eslint-disable-line

  useEffect(() => {
    doFetch();
    const t = setInterval(doFetch, 60000);
    return () => clearInterval(t);
  }, [key]); // eslint-disable-line

  return { quotes, loading, lastUpdate, error, refetch: doFetch };
}

// ─── Componentes compartilhados ───────────────────────────────
const KPI = ({ label, value, sub, tone }) => {
  const tones = {
    blue:   "from-blue-600 to-blue-800",
    green:  "from-emerald-500 to-emerald-700",
    violet: "from-violet-600 to-violet-800",
    rose:   "from-rose-600 to-rose-800",
  };
  return (
    <div className={`rounded-2xl p-3 md:p-5 bg-gradient-to-br ${tones[tone] || tones.blue} text-white overflow-hidden`}>
      <div className="text-[10px] md:text-xs uppercase tracking-wider font-semibold opacity-90 leading-tight">{label}</div>
      <div className="mt-1.5 font-display text-base md:text-2xl font-bold tabular-nums truncate">{value}</div>
      {sub && <div className="mt-0.5 text-[10px] md:text-xs opacity-80 truncate">{sub}</div>}
    </div>
  );
};

const Var = ({ pct }) => {
  if (pct === null || pct === undefined) return <span className="text-slate-400">—</span>;
  return (
    <span className={`font-bold tabular-nums ${pct >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
      {pct >= 0 ? "+" : ""}{pct.toFixed(2)}%
    </span>
  );
};

const LiveBadge = ({ loading, lastUpdate, marketOpen, error }) => {
  const t = lastUpdate?.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  if (loading) return (
    <div className="flex items-center gap-1.5 text-[10px] text-amber-400">
      <RefreshCw className="h-3 w-3 animate-spin" /> Atualizando…
    </div>
  );
  if (error && !lastUpdate) return (
    <div className="flex items-center gap-1.5 text-[10px] text-rose-400">
      <span className="h-1.5 w-1.5 rounded-full bg-rose-400 inline-block" />
      Sem conexão
    </div>
  );
  if (error && lastUpdate) return (
    <div className="flex items-center gap-1.5 text-[10px] text-amber-400">
      <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse inline-block" />
      Reconectando · última: {t}
    </div>
  );
  if (!lastUpdate) return null;
  if (marketOpen === false) return (
    <div className="flex items-center gap-1.5 text-[10px] text-slate-500">
      <span className="h-1.5 w-1.5 rounded-full bg-slate-500 inline-block" />
      Mercado fechado · {t}
    </div>
  );
  return (
    <div className="flex items-center gap-1.5 text-[10px] text-emerald-400">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse inline-block" />
      Ao vivo · {lastUpdate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
    </div>
  );
};

const TableHead = ({ cols }) => (
  <thead>
    <tr className="text-[11px] uppercase tracking-wider text-slate-400 border-b border-slate-800">
      {cols.map((c) => <th key={c} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{c}</th>)}
    </tr>
  </thead>
);

const SummaryBar = ({ items }) => (
  <div className="flex flex-wrap gap-4 md:gap-8 px-5 py-4 bg-slate-800/40 border-b border-slate-800">
    {items.map((it) => (
      <div key={it.label}>
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{it.label}</div>
        <div className={`text-base font-bold font-display tabular-nums mt-0.5 ${it.tone === "green" ? "text-emerald-300" : it.tone === "red" ? "text-rose-300" : "text-white"}`}>
          {it.value}
        </div>
      </div>
    ))}
  </div>
);

const BackBtn = ({ onBack, label }) => (
  <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white transition">
    <ChevronLeft className="h-4 w-4" /> {label || "Visão Geral"}
  </button>
);

// ─── TELA DE AÇÕES ─────────────────────────────────────────────
function AcoesView({ onBack }) {
  const { data, removeAcao } = useAppData();
  const acoes = (data.investimentos.acoes || []).filter((a) => a.tipo === "Ação" || !a.tipo);
  const [modal, setModal] = useState({ open: false, item: null });
  const tickers = acoes.map((a) => a.ticker);
  const { quotes, loading, lastUpdate, marketOpen, error, refetch } = useBrapiQuotes(tickers);
  const divItems = acoes.map((a) => ({ ticker: a.ticker, dataCompra: a.dataCompra, qtd: a.qtd }));
  const autoDiv  = useBrapiDividends(divItems);

  const rows = acoes.map((a) => {
    const livePrice = quotes[a.ticker]?.price;
    const cotacao   = livePrice ?? a.cotacao ?? a.pmc;
    const chgPct    = quotes[a.ticker]?.changePercent ?? null;
    const ct        = a.qtd * a.pmc;
    const pat       = a.qtd * cotacao;
    const divAuto   = autoDiv[a.ticker] || 0;
    const divManual = Number(a.dividendosManuais ?? a.dividendos ?? 0);
    const totalDiv  = divAuto + divManual;
    const lucro     = (pat - ct) + totalDiv;
    const variacao  = ct > 0 ? (lucro / ct) * 100 : 0;
    return { ...a, cotacaoLive: cotacao, chgPct, ct, pat, lucro, variacao, totalDiv, divAuto, divManual };
  });

  const totalCusto = rows.reduce((s, r) => s + r.ct, 0);
  const totalPat   = rows.reduce((s, r) => s + r.pat, 0);
  const totalDiv   = rows.reduce((s, r) => s + r.totalDiv, 0);
  const totalLucro = rows.reduce((s, r) => s + r.lucro, 0);
  const rentAcum   = totalCusto > 0 ? (totalLucro / totalCusto) * 100 : 0;

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-5 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <BackBtn onBack={onBack} />
            <h1 className="font-display text-2xl font-bold mt-1 flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-blue-400" /> Carteira de Ações
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <LiveBadge loading={loading} lastUpdate={lastUpdate} marketOpen={marketOpen} error={error} />
            <button onClick={refetch} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition" title="Atualizar">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button onClick={() => setModal({ open: true, item: null })} className="rounded-full h-9 px-4 text-xs font-semibold monyx-btn-primary text-white inline-flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Novo ativo
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI tone="blue"   label="Total investido"  value={brl(totalCusto)} />
          <KPI tone="green"  label="Patrimônio atual" value={brl(totalPat)} />
          <KPI tone={totalLucro >= 0 ? "green" : "rose"} label="Lucro / Prejuízo"
            value={brl(totalLucro)} sub={`Dividendos incluso: ${brl(totalDiv)}`} />
          <KPI tone="violet" label="Rentabilidade"
            value={`${rentAcum >= 0 ? "+" : ""}${rentAcum.toFixed(2)}%`}
            sub={`Capital + dividendos`} />
        </div>

        <div className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden">
          <SummaryBar items={[
            { label: "Ativos", value: `${acoes.length}` },
            { label: "Custo total", value: brl(totalCusto) },
            { label: "Patrimônio", value: brl(totalPat), tone: "green" },
            { label: "Dividendos", value: brl(totalDiv), tone: "green" },
            { label: "Lucro total", value: brl(totalLucro), tone: totalLucro >= 0 ? "green" : "red" },
          ]} />
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm min-w-[1000px]">
              <TableHead cols={["Ticker","QTD","PMC","Custo Total","Cotação","Var. Dia","Patrimônio","Dividendos","Lucro Total","Variação","Ações"]} />
              <tbody className="divide-y divide-slate-800">
                {acoes.length === 0 ? (
                  <tr><td colSpan={11} className="py-10 text-center text-slate-400 text-sm">Nenhuma ação cadastrada.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.ticker} className="hover:bg-slate-800/30 transition">
                    <td className="px-4 py-3">
                      <div className="font-mono font-bold text-emerald-300">{r.ticker}</div>
                      {r.dataCompra && <div className="text-[10px] text-slate-500">desde {r.dataCompra}</div>}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-slate-300">{r.qtd}</td>
                    <td className="px-4 py-3 tabular-nums text-slate-300">{brl(r.pmc)}</td>
                    <td className="px-4 py-3 tabular-nums text-slate-300">{brl(r.ct)}</td>
                    <td className="px-4 py-3 tabular-nums text-blue-300 font-semibold">{brl(r.cotacaoLive)}</td>
                    <td className="px-4 py-3"><Var pct={r.chgPct} /></td>
                    <td className="px-4 py-3 tabular-nums text-white font-semibold">{brl(r.pat)}</td>
                    <td className="px-4 py-3">
                      <div className="tabular-nums text-amber-300 font-semibold">{brl(r.totalDiv)}</div>
                      {r.divAuto > 0 && <div className="text-[10px] text-slate-500">auto: {brl(r.divAuto)}</div>}
                    </td>
                    <td className={`px-4 py-3 tabular-nums font-semibold ${r.lucro >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{brl(r.lucro)}</td>
                    <td className="px-4 py-3"><Var pct={r.variacao} /></td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => setModal({ open: true, item: r })} className="h-7 w-7 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-emerald-300 transition"><Pencil className="h-3 w-3" /></button>
                        <button onClick={() => { if (window.confirm(`Remover ${r.ticker}?`)) { removeAcao(r.ticker); toast.success("Ativo removido"); }}} className="h-7 w-7 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-rose-300 transition"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-slate-800">
            {acoes.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">Nenhuma ação cadastrada.</div>
            ) : rows.map((r) => (
              <div key={r.ticker} className="px-4 py-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-mono font-bold text-emerald-300 text-base">{r.ticker}</div>
                    {r.dataCompra && <div className="text-[10px] text-slate-500 mt-0.5">desde {r.dataCompra}</div>}
                    <div className="text-xs text-slate-400 mt-1">{r.qtd} un · PMC {brl(r.pmc)}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setModal({ open: true, item: r })} className="h-9 w-9 grid place-items-center rounded-xl bg-slate-800 text-slate-400 hover:text-emerald-300 transition"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => { if (window.confirm(`Remover ${r.ticker}?`)) { removeAcao(r.ticker); toast.success("Ativo removido"); }}} className="h-9 w-9 grid place-items-center rounded-xl bg-slate-800 text-slate-400 hover:text-rose-300 transition"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-slate-800/60 rounded-xl p-2 overflow-hidden">
                    <div className="text-[9px] text-slate-500 uppercase mb-1">Cotação</div>
                    <div className="text-xs font-bold text-blue-300 tabular-nums truncate">{brl(r.cotacaoLive)}</div>
                    <Var pct={r.chgPct} />
                  </div>
                  <div className="bg-slate-800/60 rounded-xl p-2 overflow-hidden">
                    <div className="text-[9px] text-slate-500 uppercase mb-1">Patrimônio</div>
                    <div className="text-xs font-bold text-white tabular-nums truncate">{brl(r.pat)}</div>
                  </div>
                  <div className="bg-slate-800/60 rounded-xl p-2 overflow-hidden">
                    <div className="text-[9px] text-slate-500 uppercase mb-1">Lucro</div>
                    <div className={`text-xs font-bold tabular-nums truncate ${r.lucro >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{brl(r.lucro)}</div>
                    <Var pct={r.variacao} />
                  </div>
                </div>
                {r.totalDiv > 0 && (
                  <div className="mt-2 text-xs text-amber-300 tabular-nums">Dividendos: {brl(r.totalDiv)}{r.divAuto > 0 ? ` (auto: ${brl(r.divAuto)})` : ""}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <AcaoModal open={modal.open} onClose={() => setModal({ open: false, item: null })} item={modal.item} tipo="Ação" />
    </AppLayout>
  );
}

// ─── TELA DE FIIs ──────────────────────────────────────────────
function FiisView({ onBack }) {
  const { data, removeAcao } = useAppData();
  const fiis = (data.investimentos.acoes || []).filter((a) => a.tipo === "FII");
  const [modal, setModal] = useState({ open: false, item: null });
  const tickers = fiis.map((a) => a.ticker);
  const { quotes, loading, lastUpdate, marketOpen, error, refetch } = useBrapiQuotes(tickers);
  const divItems = fiis.map((f) => ({ ticker: f.ticker, dataCompra: f.dataCompra, qtd: f.qtd }));
  const autoDiv  = useBrapiDividends(divItems);

  const rows = fiis.map((f) => {
    const livePrice = quotes[f.ticker]?.price;
    const cotacao   = livePrice ?? f.cotacao ?? f.pmc;
    const chgPct    = quotes[f.ticker]?.changePercent ?? null;
    const ct        = f.qtd * f.pmc;
    const pat       = f.qtd * cotacao;
    const divAuto   = autoDiv[f.ticker] || 0;
    const divManual = Number(f.dividendosManuais ?? f.dividendos ?? 0);
    const totalRend = divAuto + divManual;
    const lucro     = (pat - ct) + totalRend;
    const variacao  = ct > 0 ? (lucro / ct) * 100 : 0;
    const dy        = cotacao > 0 && totalRend > 0 ? ((totalRend / (f.qtd * cotacao)) * 100) : 0;
    return { ...f, cotacaoLive: cotacao, chgPct, ct, pat, lucro, variacao, totalRend, divAuto, dy };
  });

  const totalCusto = rows.reduce((s, r) => s + r.ct, 0);
  const totalPat   = rows.reduce((s, r) => s + r.pat, 0);
  const totalRend  = rows.reduce((s, r) => s + r.totalRend, 0);
  const totalLucro = rows.reduce((s, r) => s + r.lucro, 0);
  const rentAcum   = totalCusto > 0 ? (totalLucro / totalCusto) * 100 : 0;

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-5 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <BackBtn onBack={onBack} />
            <h1 className="font-display text-2xl font-bold mt-1 flex items-center gap-2">
              <Building2 className="h-6 w-6 text-violet-400" /> Carteira de FIIs
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <LiveBadge loading={loading} lastUpdate={lastUpdate} marketOpen={marketOpen} error={error} />
            <button onClick={refetch} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition"><RefreshCw className="h-4 w-4" /></button>
            <button onClick={() => setModal({ open: true, item: null })} className="rounded-full h-9 px-4 text-xs font-semibold monyx-btn-primary text-white inline-flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Novo FII
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI tone="violet" label="Total investido"  value={brl(totalCusto)} />
          <KPI tone="green"  label="Patrimônio atual" value={brl(totalPat)} />
          <KPI tone={totalLucro >= 0 ? "green" : "rose"} label="Lucro / Prejuízo"
            value={brl(totalLucro)} sub={`Rendimentos incluso: ${brl(totalRend)}`} />
          <KPI tone="blue"   label="Rentabilidade"
            value={`${rentAcum >= 0 ? "+" : ""}${rentAcum.toFixed(2)}%`}
            sub="Capital + rendimentos" />
        </div>

        <div className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden">
          <SummaryBar items={[
            { label: "FIIs", value: `${fiis.length}` },
            { label: "Custo total", value: brl(totalCusto) },
            { label: "Patrimônio", value: brl(totalPat), tone: "green" },
            { label: "Rendimentos", value: brl(totalRend), tone: "green" },
            { label: "Lucro total", value: brl(totalLucro), tone: totalLucro >= 0 ? "green" : "red" },
          ]} />
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm min-w-[1050px]">
              <TableHead cols={["Ticker","QTD","PMC","Custo Total","Cotação","Var. Dia","Patrimônio","Rendimentos","DY Est.","Lucro Total","Variação","Ações"]} />
              <tbody className="divide-y divide-slate-800">
                {fiis.length === 0 ? (
                  <tr><td colSpan={12} className="py-10 text-center text-slate-400 text-sm">Nenhum FII cadastrado.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.ticker} className="hover:bg-slate-800/30 transition">
                    <td className="px-4 py-3">
                      <div className="font-mono font-bold text-violet-300">{r.ticker}</div>
                      {r.dataCompra && <div className="text-[10px] text-slate-500">desde {r.dataCompra}</div>}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-slate-300">{r.qtd}</td>
                    <td className="px-4 py-3 tabular-nums text-slate-300">{brl(r.pmc)}</td>
                    <td className="px-4 py-3 tabular-nums text-slate-300">{brl(r.ct)}</td>
                    <td className="px-4 py-3 tabular-nums text-blue-300 font-semibold">{brl(r.cotacaoLive)}</td>
                    <td className="px-4 py-3"><Var pct={r.chgPct} /></td>
                    <td className="px-4 py-3 tabular-nums text-white font-semibold">{brl(r.pat)}</td>
                    <td className="px-4 py-3">
                      <div className="tabular-nums text-amber-300 font-semibold">{brl(r.totalRend)}</div>
                      {r.divAuto > 0 && <div className="text-[10px] text-slate-500">auto: {brl(r.divAuto)}</div>}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-emerald-300">{r.dy > 0 ? `${r.dy.toFixed(2)}%` : "—"}</td>
                    <td className={`px-4 py-3 tabular-nums font-semibold ${r.lucro >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{brl(r.lucro)}</td>
                    <td className="px-4 py-3"><Var pct={r.variacao} /></td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => setModal({ open: true, item: r })} className="h-7 w-7 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-violet-300 transition"><Pencil className="h-3 w-3" /></button>
                        <button onClick={() => { if (window.confirm(`Remover ${r.ticker}?`)) { removeAcao(r.ticker); toast.success("FII removido"); }}} className="h-7 w-7 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-rose-300 transition"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-slate-800">
            {fiis.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">Nenhum FII cadastrado.</div>
            ) : rows.map((r) => (
              <div key={r.ticker} className="px-4 py-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-mono font-bold text-violet-300 text-base">{r.ticker}</div>
                    {r.dataCompra && <div className="text-[10px] text-slate-500 mt-0.5">desde {r.dataCompra}</div>}
                    <div className="text-xs text-slate-400 mt-1">{r.qtd} cotas · PMC {brl(r.pmc)}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setModal({ open: true, item: r })} className="h-9 w-9 grid place-items-center rounded-xl bg-slate-800 text-slate-400 hover:text-violet-300 transition"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => { if (window.confirm(`Remover ${r.ticker}?`)) { removeAcao(r.ticker); toast.success("FII removido"); }}} className="h-9 w-9 grid place-items-center rounded-xl bg-slate-800 text-slate-400 hover:text-rose-300 transition"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-slate-800/60 rounded-xl p-2 overflow-hidden">
                    <div className="text-[9px] text-slate-500 uppercase mb-1">Cotação</div>
                    <div className="text-xs font-bold text-blue-300 tabular-nums truncate">{brl(r.cotacaoLive)}</div>
                    <Var pct={r.chgPct} />
                  </div>
                  <div className="bg-slate-800/60 rounded-xl p-2 overflow-hidden">
                    <div className="text-[9px] text-slate-500 uppercase mb-1">Patrimônio</div>
                    <div className="text-xs font-bold text-white tabular-nums truncate">{brl(r.pat)}</div>
                  </div>
                  <div className="bg-slate-800/60 rounded-xl p-2 overflow-hidden">
                    <div className="text-[9px] text-slate-500 uppercase mb-1">Lucro</div>
                    <div className={`text-xs font-bold tabular-nums truncate ${r.lucro >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{brl(r.lucro)}</div>
                    <Var pct={r.variacao} />
                  </div>
                </div>
                {r.totalRend > 0 && (
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className="text-amber-300 tabular-nums">Rendimentos: {brl(r.totalRend)}{r.divAuto > 0 ? ` (auto: ${brl(r.divAuto)})` : ""}</span>
                    {r.dy > 0 && <span className="text-emerald-300">DY {r.dy.toFixed(2)}%</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      <AcaoModal open={modal.open} onClose={() => setModal({ open: false, item: null })} item={modal.item} tipo="FII" />
    </AppLayout>
  );
}

// ─── TELA DE CRIPTOMOEDAS ─────────────────────────────────────
function CryptosView({ onBack }) {
  const { data, removeCrypto } = useAppData();
  const cryptos = data.investimentos.cryptos || [];
  const [modal, setModal] = useState({ open: false, item: null });
  const cryptoItems = [...new Map(
    cryptos.filter((c) => c.symbol).map((c) => [c.symbol, {
      symbol: c.symbol,
      geckoId: SYMBOL_TO_GECKO[c.symbol] || c.geckoId || c.symbol.toLowerCase(),
    }])
  ).values()];
  const { quotes, loading, lastUpdate, error, refetch } = useCryptoQuotes(cryptoItems);

  const rows = cryptos.map((c) => {
    const q      = quotes[c.symbol];
    const hasQ   = q && q.price > 0;
    const chg24h = q?.change24h ?? null;
    const ct     = c.qtd * c.pmc;
    const pat    = hasQ ? c.qtd * q.price : null;
    const lucro  = hasQ ? pat - ct : null;
    return { ...c, cotacaoLive: hasQ ? q.price : null, chg24h, ct, pat, lucro };
  });

  const totalCusto = rows.reduce((s, r) => s + r.ct, 0);
  const totalPat   = rows.reduce((s, r) => s + (r.pat ?? 0), 0);
  const totalLucro = rows.reduce((s, r) => s + (r.lucro ?? 0), 0);
  const rentAcum   = totalCusto > 0 ? (totalLucro / totalCusto) * 100 : 0;

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-5 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <BackBtn onBack={onBack} />
            <h1 className="font-display text-2xl font-bold mt-1 flex items-center gap-2">
              <Bitcoin className="h-6 w-6 text-amber-400" /> Criptomoedas
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <LiveBadge loading={loading} lastUpdate={lastUpdate} error={error} />
            <button onClick={refetch} className="h-8 w-8 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-white transition"><RefreshCw className="h-4 w-4" /></button>
            <button onClick={() => setModal({ open: true, item: null })} className="rounded-full h-9 px-4 text-xs font-semibold monyx-btn-primary text-white inline-flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5" /> Nova cripto
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI tone="blue"   label="Total investido"  value={brl(totalCusto)} />
          <KPI tone="green"  label="Patrimônio atual" value={totalPat > 0 ? brl(totalPat) : "—"} />
          <KPI tone={totalLucro >= 0 ? "green" : "rose"} label="Lucro / Prejuízo" value={totalPat > 0 ? brl(totalLucro) : "—"} />
          <KPI tone="violet" label="Rentabilidade"    value={totalPat > 0 ? `${rentAcum >= 0 ? "+" : ""}${rentAcum.toFixed(2)}%` : "—"} />
        </div>

        <div className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden">
          <SummaryBar items={[
            { label: "Moedas", value: `${cryptos.length}` },
            { label: "Custo total", value: brl(totalCusto) },
            { label: "Patrimônio", value: totalPat > 0 ? brl(totalPat) : "—", tone: "green" },
            { label: "Lucro", value: totalPat > 0 ? brl(totalLucro) : "—", tone: totalLucro >= 0 ? "green" : "red" },
          ]} />
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <TableHead cols={["Moeda","QTD","Preço Médio","Custo Total","Cotação (BRL)","Var. 24h","Patrimônio","Lucro","Ações"]} />
              <tbody className="divide-y divide-slate-800">
                {cryptos.length === 0 ? (
                  <tr><td colSpan={9} className="py-10 text-center text-slate-400 text-sm">Nenhuma criptomoeda cadastrada.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} className="hover:bg-slate-800/30 transition">
                    <td className="px-4 py-3">
                      <div className="font-bold text-amber-300">{r.symbol || r.moeda}</div>
                      <div className="text-[10px] text-slate-500">{r.moeda}</div>
                      {r.dataCompra && <div className="text-[10px] text-slate-600">desde {r.dataCompra}</div>}
                    </td>
                    <td className="px-4 py-3 tabular-nums text-slate-300">{r.qtd}</td>
                    <td className="px-4 py-3 tabular-nums text-slate-300">{brl(r.pmc)}</td>
                    <td className="px-4 py-3 tabular-nums text-slate-300">{brl(r.ct)}</td>
                    <td className="px-4 py-3 tabular-nums text-blue-300 font-semibold">{r.cotacaoLive > 0 ? brl(r.cotacaoLive) : "—"}</td>
                    <td className="px-4 py-3"><Var pct={r.chg24h} /></td>
                    <td className="px-4 py-3 tabular-nums text-white font-semibold">{r.pat != null ? brl(r.pat) : "—"}</td>
                    <td className={`px-4 py-3 tabular-nums font-semibold ${r.lucro == null ? "text-slate-500" : r.lucro >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{r.lucro != null ? brl(r.lucro) : "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => setModal({ open: true, item: r })} className="h-7 w-7 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-amber-300 transition"><Pencil className="h-3 w-3" /></button>
                        <button onClick={() => { if (window.confirm(`Remover ${r.moeda}?`)) { removeCrypto(r.id); toast.success("Cripto removida"); }}} className="h-7 w-7 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-rose-300 transition"><Trash2 className="h-3 w-3" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-slate-800">
            {cryptos.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">Nenhuma criptomoeda cadastrada.</div>
            ) : rows.map((r) => (
              <div key={r.id} className="px-4 py-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="font-bold text-amber-300 text-base">{r.symbol || r.moeda}</div>
                    <div className="text-xs text-slate-400">{r.moeda}</div>
                    {r.dataCompra && <div className="text-[10px] text-slate-500 mt-0.5">desde {r.dataCompra}</div>}
                    <div className="text-xs text-slate-400 mt-1">{r.qtd} · PMC {brl(r.pmc)}</div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setModal({ open: true, item: r })} className="h-9 w-9 grid place-items-center rounded-xl bg-slate-800 text-slate-400 hover:text-amber-300 transition"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => { if (window.confirm(`Remover ${r.moeda}?`)) { removeCrypto(r.id); toast.success("Cripto removida"); }}} className="h-9 w-9 grid place-items-center rounded-xl bg-slate-800 text-slate-400 hover:text-rose-300 transition"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-slate-800/60 rounded-xl p-2 overflow-hidden">
                    <div className="text-[9px] text-slate-500 uppercase mb-1">Cotação</div>
                    <div className="text-xs font-bold text-blue-300 tabular-nums truncate">{r.cotacaoLive > 0 ? brl(r.cotacaoLive) : "—"}</div>
                    <Var pct={r.chg24h} />
                  </div>
                  <div className="bg-slate-800/60 rounded-xl p-2 overflow-hidden">
                    <div className="text-[9px] text-slate-500 uppercase mb-1">Patrimônio</div>
                    <div className="text-xs font-bold text-white tabular-nums truncate">{r.pat != null ? brl(r.pat) : "—"}</div>
                  </div>
                  <div className="bg-slate-800/60 rounded-xl p-2 overflow-hidden">
                    <div className="text-[9px] text-slate-500 uppercase mb-1">Lucro</div>
                    <div className={`text-xs font-bold tabular-nums truncate ${r.lucro == null ? "text-slate-500" : r.lucro >= 0 ? "text-emerald-300" : "text-rose-300"}`}>{r.lucro != null ? brl(r.lucro) : "—"}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <CryptoModal open={modal.open} onClose={() => setModal({ open: false, item: null })} item={modal.item} />
    </AppLayout>
  );
}

// ─── TELA DE RENDA FIXA ────────────────────────────────────────
function CdiView({ onBack }) {
  const { data, removeCdi } = useAppData();
  const { cdi } = data.investimentos;
  const [cdiOpen, setCdiOpen] = useState(false);

  const totalRef  = cdi.reduce((s, c) => s + Number(c.valorRef  || 0), 0);
  const totalRend = cdi.reduce((s, c) => s + Number(c.rendimento || 0), 0);
  const totalInv  = cdi.reduce((s, c) => s + Number(c.valor     || 0), 0);
  const rentMedia = totalInv > 0 ? (totalRend / totalInv) * 100 : 0;

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-5 md:px-8 pt-5 pb-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <BackBtn onBack={onBack} />
            <h1 className="font-display text-2xl font-bold mt-1 flex items-center gap-2">
              <Landmark className="h-6 w-6 text-emerald-400" /> Renda Fixa
            </h1>
          </div>
          <button onClick={() => setCdiOpen(true)} className="rounded-full h-9 px-4 text-xs font-semibold monyx-btn-primary text-white inline-flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> Nova aplicação
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI tone="green"  label="Total investido" value={brl(totalInv)} />
          <KPI tone="blue"   label="Valor atual"     value={brl(totalRef)} />
          <KPI tone="violet" label="Rendimentos"     value={brl(totalRend)} sub="Acumulado" />
          <KPI tone="green"  label="Rentabilidade"   value={`+${rentMedia.toFixed(2)}%`} sub="Média das posições" />
        </div>

        <div className="rounded-2xl bg-slate-900 border border-slate-800 overflow-hidden">
          <SummaryBar items={[
            { label: "Posições", value: `${cdi.length}` },
            { label: "CDI atual (ref.)", value: "13,75% a.a." },
            { label: "Total ref.", value: brl(totalRef), tone: "green" },
            { label: "Rendimentos", value: brl(totalRend), tone: "green" },
          ]} />
          {/* Desktop table */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-sm min-w-[1000px]">
              <TableHead cols={["Data","Banco/Corretora","Ativo","Tipo","Valor Inv.","CDI An.","% CDI","Dias","Taxa","Rendimento","Valor Ref.","Ações"]} />
              <tbody className="divide-y divide-slate-800">
                {cdi.length === 0 ? (
                  <tr><td colSpan={12} className="py-10 text-center text-slate-400 text-sm">Nenhuma renda fixa cadastrada.</td></tr>
                ) : cdi.map((c, i) => (
                  <tr key={i} className="hover:bg-slate-800/30 transition">
                    <td className="px-4 py-3 text-xs text-slate-400 tabular-nums">{c.data}</td>
                    <td className="px-4 py-3 text-slate-200">{c.banco}</td>
                    <td className="px-4 py-3 font-semibold text-white">{c.ativo}</td>
                    <td className="px-4 py-3"><span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-[10px] font-bold">{c.tipo}</span></td>
                    <td className="px-4 py-3 tabular-nums text-slate-300">{brl(c.valor)}</td>
                    <td className="px-4 py-3 tabular-nums text-slate-400">{c.cdiAn}%</td>
                    <td className="px-4 py-3 tabular-nums text-emerald-300">{c.pctCdi}%</td>
                    <td className="px-4 py-3 tabular-nums text-slate-400">{c.dias}</td>
                    <td className="px-4 py-3 text-slate-300 text-xs">{c.taxa}</td>
                    <td className="px-4 py-3 tabular-nums text-emerald-300 font-semibold">{brl(c.rendimento)}</td>
                    <td className="px-4 py-3 tabular-nums text-white font-bold">{brl(c.valorRef)}</td>
                    <td className="px-4 py-3">
                      <button onClick={() => { if (window.confirm("Remover?")) { removeCdi(i); toast.success("Removido"); }}}
                        className="h-7 w-7 grid place-items-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-rose-300 transition"><Trash2 className="h-3 w-3" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-slate-800">
            {cdi.length === 0 ? (
              <div className="py-10 text-center text-slate-400 text-sm">Nenhuma renda fixa cadastrada.</div>
            ) : cdi.map((c, i) => (
              <div key={i} className="px-4 py-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-white text-base truncate">{c.ativo}</div>
                      <span className="px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 text-[10px] font-bold shrink-0">{c.tipo}</span>
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">{c.banco}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{c.data} · {c.dias} dias</div>
                  </div>
                  <button onClick={() => { if (window.confirm("Remover?")) { removeCdi(i); toast.success("Removido"); }}}
                    className="h-9 w-9 grid place-items-center rounded-xl bg-slate-800 text-slate-400 hover:text-rose-300 transition shrink-0 ml-2"><Trash2 className="h-4 w-4" /></button>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-slate-800/60 rounded-xl p-2 overflow-hidden">
                    <div className="text-[9px] text-slate-500 uppercase mb-1">Investido</div>
                    <div className="text-xs font-bold text-slate-200 tabular-nums truncate">{brl(c.valor)}</div>
                    <div className="text-[9px] text-slate-500 mt-0.5">{c.pctCdi}% CDI</div>
                  </div>
                  <div className="bg-slate-800/60 rounded-xl p-2 overflow-hidden">
                    <div className="text-[9px] text-slate-500 uppercase mb-1">Rendimento</div>
                    <div className="text-xs font-bold text-emerald-300 tabular-nums truncate">{brl(c.rendimento)}</div>
                    <div className="text-[9px] text-slate-500 mt-0.5 truncate">{c.taxa}</div>
                  </div>
                  <div className="bg-slate-800/60 rounded-xl p-2 overflow-hidden">
                    <div className="text-[9px] text-slate-500 uppercase mb-1">Valor Atual</div>
                    <div className="text-xs font-bold text-white tabular-nums truncate">{brl(c.valorRef)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <CdiModal open={cdiOpen} onClose={() => setCdiOpen(false)} />
    </AppLayout>
  );
}

// ─── OVERVIEW — TELA PRINCIPAL ─────────────────────────────────
function OverviewView({ onNav }) {
  const { data } = useAppData();
  const { acoes = [], cdi = [], cryptos = [] } = data.investimentos;

  const acoesItems = acoes.filter((a) => a.tipo === "Ação" || !a.tipo);
  const fiisItems  = acoes.filter((a) => a.tipo === "FII");

  // Live quotes for overview cards (uses cached acoes cotacao as fallback)
  const allTickers = acoes.map((a) => a.ticker);
  const { quotes: b3Quotes, loading: b3Loading } = useBrapiQuotes(allTickers);
  const allCryptoItems = [...new Map(
    cryptos.filter((c) => c.symbol).map((c) => [c.symbol, {
      symbol: c.symbol,
      geckoId: SYMBOL_TO_GECKO[c.symbol] || c.geckoId || c.symbol.toLowerCase(),
    }])
  ).values()];
  const { quotes: cgQuotes, loading: cgLoading } = useCryptoQuotes(allCryptoItems);

  const calcCard = (items, getPrice) => {
    let custo = 0, patrimonio = 0, dividendos = 0;
    items.forEach((a) => {
      const cotacao = getPrice(a);
      custo     += a.qtd * a.pmc;
      patrimonio += a.qtd * cotacao;
      dividendos += Number(a.dividendos || 0);
    });
    return { custo, patrimonio, lucro: patrimonio - custo, dividendos };
  };

  const acCard = calcCard(acoesItems, (a) => b3Quotes[a.ticker]?.price ?? a.cotacao ?? a.pmc);
  const fiCard = calcCard(fiisItems,  (f) => b3Quotes[f.ticker]?.price ?? f.cotacao ?? f.pmc);
  const cgCard = (() => {
    let custo = 0, patrimonio = 0, hasLiveQuote = false;
    cryptos.forEach((c) => {
      const livePrice = cgQuotes[c.symbol]?.price;
      if (livePrice > 0) hasLiveQuote = true;
      custo     += c.qtd * c.pmc;
      patrimonio += c.qtd * (livePrice > 0 ? livePrice : c.pmc);
    });
    const lucro = patrimonio - custo;
    return { custo, patrimonio, lucro, hasLiveQuote };
  })();
  const rfCard = {
    custo:      cdi.reduce((s, c) => s + Number(c.valor || 0), 0),
    patrimonio: cdi.reduce((s, c) => s + Number(c.valorRef || 0), 0),
    rendimento: cdi.reduce((s, c) => s + Number(c.rendimento || 0), 0),
  };

  const totalCusto     = acCard.custo + fiCard.custo + cgCard.custo + rfCard.custo;
  const totalPatrimonio = acCard.patrimonio + fiCard.patrimonio + cgCard.patrimonio + rfCard.patrimonio;
  const totalLucro     = totalPatrimonio - totalCusto;
  const rentTotal      = totalCusto > 0 ? (totalLucro / totalCusto) * 100 : 0;

  const navCards = [
    {
      id: "acoes", icon: <BarChart3 className="h-6 w-6" />, title: "Ações", color: "blue",
      value: brl(acCard.patrimonio), sub: `${acoesItems.length} ativos · Custo ${brl(acCard.custo)}`,
      lucro: acCard.lucro, varPct: acCard.custo > 0 ? (acCard.lucro / acCard.custo) * 100 : 0,
      loading: b3Loading,
    },
    {
      id: "fiis", icon: <Building2 className="h-6 w-6" />, title: "FIIs", color: "violet",
      value: brl(fiCard.patrimonio), sub: `${fiisItems.length} fundos · Custo ${brl(fiCard.custo)}`,
      lucro: fiCard.lucro, varPct: fiCard.custo > 0 ? (fiCard.lucro / fiCard.custo) * 100 : 0,
      loading: b3Loading,
    },
    {
      id: "cryptos", icon: <Bitcoin className="h-6 w-6" />, title: "Criptomoedas", color: "amber",
      value: cgCard.hasLiveQuote ? brl(cgCard.patrimonio) : "—",
      sub: `${cryptos.length} moedas · Custo ${brl(cgCard.custo)}`,
      lucro: cgCard.hasLiveQuote ? cgCard.lucro : null,
      varPct: cgCard.hasLiveQuote && cgCard.custo > 0 ? (cgCard.lucro / cgCard.custo) * 100 : null,
      loading: cgLoading,
    },
    {
      id: "cdi", icon: <Landmark className="h-6 w-6" />, title: "Renda Fixa", color: "emerald",
      value: brl(rfCard.patrimonio), sub: `${cdi.length} posições · CDI 13,75% a.a.`,
      lucro: rfCard.rendimento, varPct: rfCard.custo > 0 ? (rfCard.rendimento / rfCard.custo) * 100 : 0,
      loading: false,
    },
  ];

  const colorMap = {
    blue:    { bg: "from-blue-600/20 to-blue-800/10",    border: "border-blue-500/30 hover:border-blue-400/60",    icon: "bg-blue-500/20 text-blue-300",    text: "text-blue-300"    },
    violet:  { bg: "from-violet-600/20 to-violet-800/10", border: "border-violet-500/30 hover:border-violet-400/60", icon: "bg-violet-500/20 text-violet-300", text: "text-violet-300" },
    amber:   { bg: "from-amber-500/20 to-amber-700/10",   border: "border-amber-500/30 hover:border-amber-400/60",   icon: "bg-amber-500/20 text-amber-300",   text: "text-amber-300"   },
    emerald: { bg: "from-emerald-600/20 to-emerald-800/10",border: "border-emerald-500/30 hover:border-emerald-400/60",icon:"bg-emerald-500/20 text-emerald-300",text: "text-emerald-300"},
  };

  return (
    <AppLayout>
      <PageHeader title="Meus Investimentos" subtitle="Visão consolidada · Ações, FIIs, Cripto e Renda Fixa" testid="investimentos" />
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-6 space-y-6">

        {/* KPIs consolidados */}
        <div className="grid md:grid-cols-3 gap-4">
          <KPI tone="blue"   label="Custo / Preço Médio" value={brl(totalCusto)}       sub="Total aportado em todos os ativos" />
          <KPI tone="green"  label="Patrimônio Hoje"     value={brl(totalPatrimonio)}   sub={`Var. ${rentTotal.toFixed(2)}% vs. custo`} />
          <KPI tone={rentTotal >= 0 ? "violet" : "rose"} label="Rentabilidade"
            value={`${rentTotal >= 0 ? "+" : ""}${rentTotal.toFixed(2)}%`}
            sub={`Lucro/Prej.: ${brl(totalLucro)}`} />
        </div>

        {/* Cards de navegação */}
        <div>
          <h2 className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-3">Navegar por tipo de ativo</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {navCards.map((nc) => {
              const cfg = colorMap[nc.color];
              return (
                <button
                  key={nc.id}
                  onClick={() => onNav(nc.id)}
                  className={`text-left rounded-2xl p-5 bg-gradient-to-br ${cfg.bg} border ${cfg.border} transition group`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className={`h-11 w-11 rounded-xl grid place-items-center ${cfg.icon}`}>{nc.icon}</div>
                    {nc.loading && <RefreshCw className="h-3.5 w-3.5 text-slate-500 animate-spin" />}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">{nc.title}</div>
                  <div className="mt-1 font-display text-2xl font-bold text-white tabular-nums">{nc.value}</div>
                  <div className="mt-1 text-xs text-slate-400">{nc.sub}</div>
                  {nc.lucro != null ? (
                    <div className={`mt-2 flex items-center gap-1 text-xs font-bold ${nc.lucro >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                      {nc.lucro >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                      {nc.varPct >= 0 ? "+" : ""}{nc.varPct.toFixed(2)}% · {brl(nc.lucro)}
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-slate-500">Cotação indisponível</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}

// ─── Main ──────────────────────────────────────────────────────
export default function Investimentos() {
  const [view, setView] = useState("overview");

  if (view === "acoes")   return <AcoesView   onBack={() => setView("overview")} />;
  if (view === "fiis")    return <FiisView     onBack={() => setView("overview")} />;
  if (view === "cryptos") return <CryptosView  onBack={() => setView("overview")} />;
  if (view === "cdi")     return <CdiView      onBack={() => setView("overview")} />;
  return <OverviewView onNav={setView} />;
}
